import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './contract-info-card.component.scss';
import { Contract, downloadFile } from './contracts-table.component';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { downloadEmploymentContract } from '../features/employment-contract/employment-contract.slice';

const blobURL = import.meta.env.VITE_API_IMAGE_URL;

const workAgrementSwedish = `${blobURL}/Allmänt avtal för Egenanställning.pdf`;
const workAgrementEnglish = `${blobURL}/General Contract for Self-Employment.pdf`;

/* ---- Inline lucide-style icons (repo has no lucide-react dependency) ---- */
type IconProps = { size?: number; className?: string };
const stroke = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const SparklesIcon = ({ size = 16, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
    <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
  </svg>
);

const FileTextIcon = ({ size = 20, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M16 13H8" /><path d="M16 17H8" /><path d="M10 9H8" />
  </svg>
);

const DownloadIcon = ({ size = 16, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" x2="12" y1="15" y2="3" />
  </svg>
);

const ShieldCheckIcon = ({ size = 20, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

/* Render the brand word "Invozio" inside the heading with a gradient accent,
   while keeping the full translated string intact for every locale. */
const renderBrandTitle = (title: string) => {
  const parts = title.split(/(Invozio)/i);
  return parts.map((part, i) =>
    /^invozio$/i.test(part) ? (
      <span key={i} className="contract-info-card__brand">{part}</span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

/* Turn any email address in the translated copy into a styled mailto link. */
const linkifyEmails = (text: string) => {
  const parts = text.split(/([\w.+-]+@[\w-]+\.[\w.-]+)/g);
  return parts.map((part, i) =>
    /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(part) ? (
      <a key={i} className="contract-info-card__email-link" href={`mailto:${part}`}>{part}</a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

const ContractInfoSkeleton = () => (
  <div className="contract-info-card contract-info-card--loading" aria-hidden="true">
    <div className="ci-skel ci-skel--hero" />
    <div className="contract-info-card__options">
      <div className="ci-skel ci-skel--card" />
      <div className="ci-skel ci-skel--card" />
    </div>
  </div>
);

export const ContractInfoCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const language =
    new URLSearchParams(window.location.search).get('lang') || 'en-us';

  const contractInfo = useSelector((state: any) => state.employmentContract.contractInfo);
  const isDownloading = useSelector((state: any) => state.employmentContract.isDownloading);
  const isContractCreated = contractInfo?.isContractCreated === true;

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setLoading(false), 900);
    return () => clearTimeout(id);
  }, []);

  const handleEmploymentClick = () => {
    navigate(`/v2/employment-contract?lang=${language === 'sv-se' ? 'sv-se' : 'en-us'}`);
  };

  const handleDownloadClick = () => {
    dispatch(downloadEmploymentContract({
      contractId: contractInfo?.contractId ?? contractInfo?.id,
      lang: language,
    }));
  };

  const [contracts] = useState<Contract[]>([
    {
      id: '1',
      document: 'GENERAL_DOCUMENT',
      duration: '12/05/2023 - 11/09/2024',
      fileUrl:
        language === 'sv-se'
          ? workAgrementSwedish
          : workAgrementEnglish,
    },
  ]);

  const handleDownloadAgreement = () => {
    const fileUrl = contracts[0]?.fileUrl;
    if (fileUrl) {
      downloadFile(fileUrl, `${t('GENERAL_DOCUMENT')}.pdf`);
    }
  };

  if (loading) return <ContractInfoSkeleton />;

  return (
    <div className="contract-info-card">
      {/* ---- Hero header ---- */}
      <header className="contract-info-card__hero ci-reveal" style={{ animationDelay: '100ms' }}>
        <span className="contract-info-card__hero-blob contract-info-card__hero-blob--one" aria-hidden="true" />
        <span className="contract-info-card__hero-blob contract-info-card__hero-blob--two" aria-hidden="true" />
        <div className="contract-info-card__hero-content">
          <span className="contract-info-card__badge">
            <SparklesIcon size={14} />
            {t('CONTRACT_CENTER', { defaultValue: 'Contract Center' })}
          </span>
          <h1 className="contract-info-card__title">
            {renderBrandTitle(t('CONTRACT_WITH_INVOZIO'))}
          </h1>
          <p className="contract-info-card__hero-subtitle">
            {t('CONTRACT_HERO_SUBTITLE', {
              defaultValue:
                'Manage your employment terms, sign agreements, and craft contracts — all in one beautifully organized workspace.',
            })}
          </p>
        </div>
      </header>

      <div className="contract-info-card__options">
        {/* BOX 1 – Employment Contract */}
        <section className="contract-info-card__option-card ci-reveal" style={{ animationDelay: '200ms' }}>
          <div className="contract-info-card__option-head">
            <span className="contract-info-card__icon-box">
              <FileTextIcon size={20} />
            </span>
            <h3 className="contract-info-card__option-title">
              {t('EMPLOYMENT_CONTRACT')}
            </h3>
          </div>

          <p className="contract-info-card__option-description">
            {linkifyEmails(t('CONTRACT_DESCRIPTION'))}
          </p>

          <div className="contract-info-card__option-action">
            {isContractCreated ? (
              <button
                className="contract-info-card__primary-btn"
                onClick={handleDownloadClick}
                disabled={isDownloading}
              >
                <DownloadIcon size={16} />
                {isDownloading
                  ? (t('LOADING') || 'Loading...')
                  : (t('DOWNLOAD_EMPLOYMENT_CONTRACT') || 'Download Employment Contract')}
              </button>
            ) : (
              <button
                className="contract-info-card__primary-btn"
                onClick={handleEmploymentClick}
              >
                <DownloadIcon size={16} />
                {t('EMPLOYMENT_CONTRACT_1')}
              </button>
            )}
          </div>
        </section>

        {/* BOX 2 – Invozio Agreement */}
        <section className="contract-info-card__option-card ci-reveal" style={{ animationDelay: '300ms' }}>
          <div className="contract-info-card__option-head">
            <span className="contract-info-card__icon-box contract-info-card__icon-box--shield">
              <ShieldCheckIcon size={20} />
            </span>
            <h3 className="contract-info-card__option-title">
              {t('INVOZIO_AGREEMENT')}
            </h3>
          </div>

          <p className="contract-info-card__option-description">
            {t('agrself')}
          </p>

          <div className="contract-info-card__option-action">
            <button
              className="contract-info-card__primary-btn"
              onClick={handleDownloadAgreement}
            >
              <DownloadIcon size={16} />
              {t('DOWNLOAD_INVOZIO_AGREEMENT', { defaultValue: 'Download Invozio Agreement' })}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
